/**
 * The Supabase client.
 *
 * The anon key ships to the browser and that is fine — it is designed to be
 * public. Row Level Security is what actually protects the data (see
 * migrations 0200–0700). If RLS were off, this key would be a full database
 * handout; with RLS on, it grants exactly what the policies allow.
 *
 * The service_role key must NEVER appear in this directory. It bypasses RLS
 * entirely. It lives only in Supabase function secrets.
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Fail at boot rather than with a confusing 401 on the first query.
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. ' +
      'Copy .env.example to apps/web/.env.local and fill them in.',
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // Needed so the magic-link / OAuth redirect lands back in a session.
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
  global: {
    headers: { 'x-application-name': 'sreerasa-web' },
  },
});

/** Public URL for a file in a public bucket. Returns null for a null path. */
export function storageUrl(bucket: string, path: string | null | undefined): string | null {
  if (!path) return null;
  // Already absolute (seed data, external images) — pass through.
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
}

export function productImageUrl(path: string | null | undefined): string | null {
  return storageUrl('product-media', path);
}

export function farmPhotoUrl(path: string | null | undefined): string | null {
  return storageUrl('farm-photos', path);
}
